import React from "react";
import {
  getUnreadCount,
  getNotifications,
  markRead,
} from "../../api/notifications";
import { useAuth } from "../../providers/AuthContext";
import { formatDateTimeWithAMPM } from "../../utils/dateFormat";

type NotificationItem = {
  id: number;
  type?: string;
  title?: string;
  Title?: string;
  body?: string;
  Body?: string;
  isRead?: boolean;
  IsRead?: boolean;
  createdAt?: string;
  CreatedAt?: string;
  metadataJson?: string | null;
  MetadataJson?: string | null;
};

const POLL_MS = 15000;
const PAGE_SIZE = 20;

const Inbox: React.FC = () => {
  const { token } = useAuth(); // ✅ must exist in AuthContext (or replace with isAuthenticated)

  const [open, setOpen] = React.useState(false);
  const [count, setCount] = React.useState(0);
  const [items, setItems] = React.useState<NotificationItem[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [loadingMore, setLoadingMore] = React.useState(false);
  const [hasMore, setHasMore] = React.useState(false);

  const rootRef = React.useRef<HTMLDivElement | null>(null);
  const itemsRef = React.useRef<NotificationItem[]>([]);
  
  // Keep ref in sync with state
  React.useEffect(() => {
    itemsRef.current = items;
  }, [items]);

  const normalize = React.useCallback((n: NotificationItem) => {
    return {
      ...n,
      id: n.id,
      title: (n.title ?? n.Title ?? "").toString(),
      body: (n.body ?? n.Body ?? "").toString(),
      isRead: Boolean(n.isRead ?? n.IsRead),
      createdAt: (n.createdAt ?? n.CreatedAt ?? "") as string,
      metadataJson: (n.metadataJson ?? n.MetadataJson ?? null) as string | null,
    };
  }, []);

  const refreshUnread = React.useCallback(async () => {
    if (!token) return; // ✅ prevents signin loop
    const c = await getUnreadCount();
    setCount(c);
  }, [token]);

  const loadList = React.useCallback(async (reset = true) => {
    if (!token) return; // ✅ prevents signin loop
    
    if (reset) {
      setLoading(true);
      setItems([]);
    }
    
    try {
      const data = await getNotifications(0, PAGE_SIZE);
      const normalized = (data.items ?? []).map(normalize);
      
      setItems(normalized);
      setCount(data.unread ?? 0);
      setHasMore(normalized.length === PAGE_SIZE);
    } finally {
      setLoading(false);
    }
  }, [token, normalize]);

  const loadMore = React.useCallback(async () => {
    if (!token || loadingMore || !hasMore) return;
    
    setLoadingMore(true);
    try {
      const data = await getNotifications(itemsRef.current.length, PAGE_SIZE);
      const normalized = (data.items ?? []).map(normalize);
      
      setItems((prev) => [...prev, ...normalized]);
      setHasMore(normalized.length === PAGE_SIZE);
    } finally {
      setLoadingMore(false);
    }
  }, [token, loadingMore, hasMore, normalize]);

  // ✅ Poll unread count while logged in
  React.useEffect(() => {
    if (!token) return;

    refreshUnread();
    const t = window.setInterval(refreshUnread, POLL_MS);
    return () => window.clearInterval(t);
  }, [token, refreshUnread]);

  // ✅ Close on outside click + Esc
  React.useEffect(() => {
    if (!open) return;

    const onPointerDown = (e: PointerEvent) => {
      const el = rootRef.current;
      if (el && !el.contains(e.target as Node)) {
        setOpen(false);
      }
    };

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };

    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  const toggleOpen = async () => {
    if (!token) return; // ✅ prevents signin loop
    if (open) {
      setOpen(false);
      return;
    }
    setOpen(true);
    await loadList(true); // ✅ refresh list and badge immediately on open
  };

  const handleMarkAllRead = async () => {
    const unreadIds = items.filter((i) => !i.isRead).map((i) => i.id);
    if (unreadIds.length === 0) return;

    await markRead(unreadIds);

    setItems((prev) => prev.map((i) => ({ ...i, isRead: true })));
    setCount(0);
    
    // Refresh to get accurate unread count in case there are more notifications
    await refreshUnread();
  };

  const handleOpenNotification = async (n: NotificationItem) => {
    // ✅ mark single as read on click
    if (!n.isRead) {
      try {
        await markRead([n.id]);
        setItems((prev) =>
          prev.map((x) => (x.id === n.id ? { ...x, isRead: true } : x)),
        );
        setCount((c) => Math.max(0, c - 1));
      } catch {
        // ignore (optional toast)
      }
    }

    // ✅ Do whatever you want on click (navigate, open modal, etc.)
    // Example: if (meta?.auctionId) navigate(`/auctions/${meta.auctionId}`)
  };

  const parseMeta = (n: NotificationItem) => {
    try {
      const raw = n.metadataJson;
      if (!raw) return null;
      return JSON.parse(raw);
    } catch {
      return null;
    }
  };

  // ✅ Optional: hide icon when logged out
  if (!token) return null;

  return (
    <div className="relative flex-shrink-0" ref={rootRef}>
      <button
        className="relative rounded-md p-2 hover:bg-gray-100 overflow-visible"
        onClick={toggleOpen}
        title="Inbox"
        type="button"
      >
        {/* envelope icon */}
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M18 11.1667V16.1667L20 18.1667V19.1667H4V18.1667L6 16.1667V11.1667C6 8.08669 7.63 5.52669 10.5 4.84669V2.66669H13.5V4.84669C16.36 5.52669 18 8.09669 18 11.1667ZM14 20.1667C14 21.2667 13.1 22.1667 12 22.1667C10.89 22.1667 10 21.2667 10 20.1667H14Z"
            fill="#BFBFBF"
          />
        </svg>

        {count > 0 && (
          <span className="absolute top-0 right-0 text-[8px] bg-orange-500 text-white rounded-full px-2 py-0.5 leading-none">
            {count > 99 ? "99+" : count}
          </span>
        )}
      </button>

      {open && (
        <div className="fixed sm:absolute left-4 right-4 sm:left-auto sm:right-0 mt-2 sm:w-96 bg-white shadow-lg ring-1 ring-gray-200 rounded-md z-[60]">
          <div className="flex items-center justify-between px-3 py-2 border-b">
            <div className="font-medium">Messages</div>
            <button
              className="text-xs text-primary hover:underline disabled:opacity-40"
              onClick={handleMarkAllRead}
              disabled={items.every((i) => i.isRead)}
              type="button"
            >
              Mark all as read
            </button>
          </div>

          <div className="max-h-80 overflow-auto">
            {loading && (
              <div className="p-3 text-sm text-gray-500">Loading…</div>
            )}

            {!loading && items.length === 0 && (
              <div className="p-3 text-sm text-gray-500">No messages.</div>
            )}

            <ul className="divide-y">
              {items.map((raw) => {
                const n = normalize(raw);
                const meta = parseMeta(n);

                return (
                  <li
                    key={n.id}
                    role="button"
                    tabIndex={0}
                    onClick={() => handleOpenNotification(n)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        handleOpenNotification(n);
                      }
                    }}
                    className={[
                      "p-3 cursor-pointer hover:bg-gray-50 outline-none",
                      !n.isRead ? "bg-amber-50" : "",
                    ].join(" ")}
                  >
                    <div className="text-sm font-medium">{n.title}</div>
                    <div className="text-xs text-gray-600 mt-0.5">{n.body}</div>

                    {meta?.company && (
                      <div className="text-xs text-gray-500 mt-1">
                        Company: {meta.company.name} • {meta.company.email} •{" "}
                        {meta.company.phone}
                      </div>
                    )}

                    {meta?.owner && (
                      <div className="text-xs text-gray-500 mt-1">
                        Customer: {meta.owner.name} • {meta.owner.email} •{" "}
                        {meta.owner.phone}
                      </div>
                    )}

                    <div className="text-[11px] text-gray-400 mt-1">
                      {n.createdAt ? formatDateTimeWithAMPM(n.createdAt) : ""}
                    </div>
                  </li>
                );
              })}
            </ul>
            
            {!loading && hasMore && (
              <div className="p-3 border-t">
                <button
                  onClick={loadMore}
                  disabled={loadingMore}
                  className="w-full text-sm text-primary hover:underline disabled:opacity-50"
                  type="button"
                >
                  {loadingMore ? "Loading..." : "Load More"}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Inbox;
